import React, { useId } from 'react';

export interface LogoProps {
  /** CSS class for sizing (default: "w-8 h-8") */
  className?: string;
  /** Logo variant - 'icon' for transparent background, 'contained' for white rounded background */
  variant?: 'icon' | 'contained';
}

/**
 * mbbe Brand Logo SVG component
 * Uses the official brand icon with blue gradient
 * @example <Logo className="w-6 h-6" variant="icon" />
 * @example <Logo className="h-8 w-8" variant="contained" />
 */
export const Logo: React.FC<LogoProps> = ({ className = "w-8 h-8", variant = 'icon' }) => {
  const uniqueId = useId();
  const grad1Id = `mbbe-grad-1-${uniqueId}`;
  const grad2Id = `mbbe-grad-2-${uniqueId}`;
  const grad3Id = `mbbe-grad-3-${uniqueId}`;
  const grad4Id = `mbbe-grad-4-${uniqueId}`;

  const svgContent = (
    <svg
      viewBox="0 0 500 500"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={variant === 'contained' ? 'w-full h-full' : className}
    >
      <path d="M288.818 80.4883L157.062 163.93C157.062 163.93 134.811 177.94 137.249 201.359L137.078 255.03L269.726 169.836C269.726 169.836 288.509 161.836 288.853 129.146V80.4883H288.818Z" fill={`url(#${grad1Id})`}/>
      <path d="M184.671 224.09L222.409 250.702C222.409 250.702 238.411 262.514 238.411 282.877V341.286L158.986 286.86C158.986 286.86 136.22 271.923 137.078 254.685L184.671 224.09Z" fill={`url(#${grad2Id})`}/>
      <path d="M362.954 263.719V325.596C362.954 325.596 359.005 343.899 341.184 354.784L211.145 436.612V379.611C211.145 379.611 216.021 355.986 230.614 347.401L362.954 263.719Z" fill={`url(#${grad3Id})`}/>
      <path d="M362.508 263.718C362.508 263.718 368.757 247.511 336.102 226.874L261.004 175.641L262.171 242.841C262.171 242.841 270.035 262.757 277.005 266.259L316.392 292.906L362.508 263.718Z" fill={`url(#${grad4Id})`}/>
      <defs>
        <linearGradient id={grad1Id} x1="137.036" y1="167.764" x2="288.804" y2="167.764" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2B9FFE"/><stop offset="1" stopColor="#1A5FF9"/>
        </linearGradient>
        <linearGradient id={grad2Id} x1="291.623" y1="301.474" x2="108.056" y2="258.364" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2B9FFE"/><stop offset="1" stopColor="#1A5FF9"/>
        </linearGradient>
        <linearGradient id={grad3Id} x1="376.533" y1="230.229" x2="223.562" y2="436.046" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2B9FFE"/><stop offset="1" stopColor="#1A5FF9"/>
        </linearGradient>
        <linearGradient id={grad4Id} x1="181.595" y1="191.151" x2="381.849" y2="271.808" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2B9FFE"/><stop offset="1" stopColor="#1A5FF9"/>
        </linearGradient>
      </defs>
    </svg>
  );

  if (variant === 'contained') {
    return (
      <div className={`bg-white rounded-xl flex items-center justify-center overflow-hidden ${className}`}>
        <div className="w-[60%] h-[60%]">
          {svgContent}
        </div>
      </div>
    );
  }

  return svgContent;
};

export default Logo;
